import CryptoJS from 'crypto-js';
import { getVideoRatio, initDevice } from "../../../../utils";
import { documentValidator, validateSignature } from "../../../../services/documentValidator";
import { EVENT_TRACER } from "../../../../constants";
import { onReportEvent } from "../index";
import {SDK_EVENT_KEYS} from "../../../../utils/logService";

export function isMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

const cv = window.cv;
// MODIFICADO: Cambiado de 500ms a 300ms para enviar imágenes cada 300ms al servicio documentValidator
const MAX_FPS_DETECTION_VALUE = 700;

export class Detector {
    static croppedImage = true;
    static hasResponseToValidation = false;

    constructor({ stream, device, modelPath, croppedImage, urlService, isFront, documentType, processId, useRegula, config }) {
        this.probabilityThreshold = 50;
        this.counterSuccessfulResults = 0;
        this.MAX_FPS_DETECTION = MAX_FPS_DETECTION_VALUE;
        this.rendering = {
            now: 0,
            elapsed: 0,
            then: 0,
        };
        this.MAX_NUMBER_FACES = 1;
        this.MAX_ANGLE_TURN_HEAD = 30;
        this.MIN_FACE_SIZE_FOR_MOBILE = 224;
        this.MIN_FACE_SIZE_FOR_DESKTOP = 350;
        this.MIN_OCCUPIED_SPACE_FOR_DESKTOP = 15;
        this.MIN_OCCUPIED_SPACE_FOR_MOBILE = 15;
        this.X_OFFSET_FROM_FRAME = 15;
        this.Y_OFFSET_FROM_FRAME = 5;
        this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING = 1;
        this.videoRatio = 1;
        this.showDetectedFace = false;
        this.headTurnCounter = 0;
        this.counterSuccessfulHeadTurns = 0;
        this.stream = stream;
        this.isMobile = device.isMobile;
        this.modelPath = modelPath;
        this.croppedImage = croppedImage;
        this.urlService = urlService;
        this.documentType = documentType;
        this.isFront = isFront;
        this.processId = processId;
        this.useRegula = useRegula;
        this.config = config;
        // AGREGADO: Variables para batch mode en automático
        this.batchFrames = [];
        this.batchSize = 5;
        this.isCollectingBatch = false;
    }

    setProbabilityThreshold(val) {
        this.probabilityThreshold = val;
    }

    updateConfig(config) {
        this.config = config;
    }

    updateAllProperties(detectorProperties) {
        if (detectorProperties.urlService !== undefined) this.urlService = detectorProperties.urlService;
        if (detectorProperties.documentType !== undefined) this.documentType = detectorProperties.documentType;
        if (detectorProperties.isFront !== undefined) this.isFront = detectorProperties.isFront;
        if (detectorProperties.processId !== undefined) this.processId = detectorProperties.processId;
        if (detectorProperties.useRegula !== undefined) this.useRegula = detectorProperties.useRegula;
        if (detectorProperties.config !== undefined) this.config = detectorProperties.config;
    }

    static getInstance(detectorProperties) {
        if (!Detector.instance) {
            Detector.instance = new Detector(detectorProperties);
        } else if (detectorProperties) {
            Detector.instance.updateAllProperties(detectorProperties);
        }
        return Detector.instance;
    }

    updateHtmlElements(videoElement) {
        this.videoElement = videoElement;
    }

    static async initDetector(modelPath) {
        if (!isMobile()) {
            await this.loadingModels(modelPath);
        }
    }

    static async loadingModels(modelPath) {
        if (!this.modelsLoaded()) {
        }
    }

    static modelsLoaded() {
        return false;
    }

    static async warmingUpModels() {
    }

    static async generateHash(blobImage, processId) {
        return new Promise(async (resolve, reject) => {
            try {
                const arrayBuffer = await blobImage.blob.arrayBuffer();

                if (!arrayBuffer || arrayBuffer.byteLength === 0) {
                    throw new Error('Image arrayBuffer is empty');
                }

                const uint8Array = new Uint8Array(arrayBuffer);
                const processIdBytes = new TextEncoder().encode(processId);
                const combinedData = new Uint8Array(processIdBytes.length + uint8Array.length);
                combinedData.set(processIdBytes, 0);
                combinedData.set(uint8Array, processIdBytes.length);

                const wordArray = CryptoJS.lib.WordArray.create(combinedData);
                const hash = CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Base64);

                resolve({
                    hash,
                    preparedFile: blobImage.blob,
                });
            } catch (e) {
                reject(e);
            }
        });
    }

    static async prepareImageForUpload(blobImage, processId) {
        return new Promise(async (resolve, reject) => {
            try {
                const { hash, preparedFile } = await this.generateHash(blobImage, processId);

                const signatureResponse = await fetch('https://api-dev.ado-tech.com/api/key/generate-signature', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ hash, durationMinutes: 30, processId })
                });

                if (!signatureResponse.ok) {
                    const errorText = await signatureResponse.text();
                    throw new Error(`Failed to get signature: ${errorText}`);
                }

                const { signature, expiresAt } = await signatureResponse.json();

                if (!signature) {
                    throw new Error('Signature is empty or invalid');
                }

                resolve({ preparedFile, hash, signature, expiresAt, processId });
            } catch (e) {
                reject(e);
            }
        });
    }

    static async resizeBase64(base64, width, height, quality, changeQuality = true) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                if (changeQuality) {
                    canvas.width = width;
                    canvas.height = height;
                    // MODIFICADO: Se removieron las optimizaciones de calidad
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);
                } else {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    // MODIFICADO: Se removieron las optimizaciones de calidad
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0);
                }

                canvas.toBlob(
                    (blob) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            resolve({
                                blob,
                                base64: reader.result
                            });
                        };
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    },
                    // MODIFICADO: Cambiado de 'image/webp' con calidad 1.0 a JPEG con calidad estándar
                    'image/jpeg',
                    0.92
                );
            };
            img.onerror = reject;
            img.src = base64;
        });
    }

    static async captureFrameAsBlob(videoSrc) {
        const width = videoSrc.videoWidth;
        const height = videoSrc.videoHeight;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');
        context.drawImage(videoSrc, 0, 0, width, height);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/webp', 0.95);
        });
    }

    static async detectAllFaces(videoSrc, croppedImage, urlService, documentType, isFront, processId, useRegula, config) {
        if (this.hasResponseToValidation) {
            return;
        }
        this.hasResponseToValidation = true;

        let result = {
            isCard: false,
            image: '',
            documentName: '',
            documentMatch: false,
            status: 'no_detected'
        };

        try {
            onReportEvent(EVENT_TRACER.SERVICE.InitDetection, "Iniciando detección").catch();

            const width = videoSrc.videoWidth;
            const height = videoSrc.videoHeight;

            const originalCanvas = document.createElement('canvas');
            originalCanvas.width = width;
            originalCanvas.height = height;

            // MODIFICADO: Se removieron las optimizaciones de calidad de imagen
            // El contexto se crea sin configuraciones especiales de calidad
            const contextOriginal = originalCanvas.getContext('2d');
            contextOriginal.drawImage(videoSrc, 0, 0, originalCanvas.width, originalCanvas.height);

            // MODIFICADO: Se captura la imagen sin aplicar mejoras de calidad
            // Cambiado de 'image/webp' con calidad 1.0 a formato estándar
            const originalImage = originalCanvas.toDataURL('image/jpeg', 1);
            onReportEvent(EVENT_TRACER.SERVICE.CreateBase64Image, "Iniciando proceso de creación de base64").catch();

            const blobImage = await this.resizeBase64(originalImage, width, height, 1);
            onReportEvent(EVENT_TRACER.SERVICE.Base64ImageCreated, "Base64 de la imagen creada").catch();

            if (processId && processId.length > 0) {
                onReportEvent(EVENT_TRACER.SERVICE.SignImage, "Iniciando proceso de firmado de imagen").catch();

                const { preparedFile, hash, signature, expiresAt } = await this.prepareImageForUpload(blobImage, processId);
                onReportEvent(EVENT_TRACER.SERVICE.ImageSigned, "Finalizo proceso de firmado de imagen").catch();

                onReportEvent(EVENT_TRACER.SERVICE.InitValidateSignature, "Iniciando proceso de validación de firma de imagen").catch();

                const responseSignature = await validateSignature({
                    preparedFile,
                    signature,
                    hash,
                    expiresAt,
                    processId
                });

                onReportEvent(EVENT_TRACER.SERVICE.EndValidateSignature, "Finalizó proceso de validación de firma de imagen").catch();

                if (responseSignature.status !== 200 || responseSignature.data !== 'Valida Sign') {
                    onReportEvent(EVENT_TRACER.SERVICE.FailedValidateSignature, "La firma de la imagen no es valida").catch();
                    this.hasResponseToValidation = false;
                    return result;
                }
            }

            onReportEvent(EVENT_TRACER.SERVICE.SendingImageToValidate, "Enviando imagen al backend").catch();

            const response = await documentValidator({
                base64Data: blobImage.blob,
                urlService,
                isFront,
                documentType,
                processId,
                useRegula,
                config: config,
            });

            onReportEvent(
                EVENT_TRACER.SERVICE.SendingImageToValidateFinish,
                `Respuesta del backend (${response.data.status || 'no_detected'})`
            ).catch();
            const responseToQuality = response.data
            delete responseToQuality.images
            onReportEvent(SDK_EVENT_KEYS.ServiceResponseQuality, {data: JSON.stringify(responseToQuality)}, 'CardCapture').catch()


            this.hasResponseToValidation = false;
            result.isCard = response.data.quality;
            result.image = blobImage.base64;
            if (config?.documentValidator) {
               if (config?.documentValidator?.return_original) {
                   result.image = response?.data?.images?.original || blobImage?.base64;
               }
            }
            result.documentMatch = response.data.documentTypeMatch;
            result.status = response.data.status || 'no_detected';

            if (this.instance && this.instance.stream) {
                this.instance.stream.returnStatus(result.status);
            }

            if (response.data.quality) {
                result.documentName = response?.data?.result?.documentName;
                result.status = 'detected';
            } else {
                result.documentName = "";
                result.status = 'no_detected';
            }

            return result;
        } catch (e) {
            result.status = 'error';
            if (this.instance && this.instance.stream) {
                this.instance.stream.returnStatus('error');
            }
            return result;
        }
    }

    async startDetector() {
        if (!this.videoElement) {
            throw Error("Video element not found");
        }
        if (this.videoElement.clientWidth === 0) {
            throw Error("Video element too small");
        }

        this.rendering.then = Date.now();
        this.counterSuccessfulResults = 0;
        this.updateForIos();
        this.videoRatio = getVideoRatio(this.videoElement);

        if (!Detector.modelsLoaded()) {
            await Detector.initDetector(this.modelPath);
        }

        this.requestAnimationFrame = requestAnimationFrame(() => this.detection());
    }

    stopDetector() {
        if (this.requestAnimationFrame) {
            cancelAnimationFrame(this.requestAnimationFrame);
            this.requestAnimationFrame = undefined;
            if (this.innerCanvas) this.innerCanvas.remove();
            this.innerCanvas = undefined;
        }
    }

    updateForIos() {
        const device = initDevice();
        if (device.isIos) {
            this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING = 1;
            this.MAX_FPS_DETECTION = MAX_FPS_DETECTION_VALUE;
            Detector.isIos = true;
        }
    }

    async detection() {
        this.requestAnimationFrame = requestAnimationFrame(() => this.detection());
        this.rendering.now = Date.now();
        this.rendering.elapsed = this.rendering.now - this.rendering.then;

        if (this.rendering.elapsed > this.MAX_FPS_DETECTION) {
            this.rendering.then = this.rendering.now - (this.rendering.elapsed % this.MAX_FPS_DETECTION);

            // MODIFICADO: Verificar si enable_batch está activado
            const enableBatch = this.config?.documentValidator?.enable_batch === 'true';

            if (enableBatch && !this.isCollectingBatch) {
                // Modo batch: capturar 4-5 frames en webp antes de enviar
                const frameBlob = await Detector.captureFrameAsBlob(this.videoElement);
                this.batchFrames.push(frameBlob);

                if (this.batchFrames.length >= this.batchSize) {
                    // Enviar batch de frames al servicio
                    this.isCollectingBatch = true;
                    try {
                        onReportEvent(EVENT_TRACER.SERVICE.SendingImageToValidate, "Enviando batch de imágenes al backend").catch();

                        const response = await documentValidator({
                            base64Data: null,
                            urlService: this.urlService,
                            isFront: this.isFront,
                            documentType: this.documentType,
                            processId: this.processId,
                            config: this.config,
                            batchImages: this.batchFrames
                        });

                        onReportEvent(EVENT_TRACER.SERVICE.SendingImageToValidateFinish, `Respuesta del backend (${response.data.status || 'no_detected'})`).catch();

                        if (response.data.quality && response.status === 200) {
                            // Crear resultado con la imagen original
                            const detectionResult = {
                                isCard: true,
                                image: response.data.images?.original || '',
                                documentName: response?.data?.result?.documentName || '',
                                documentMatch: response.data.documentTypeMatch,
                                status: 'detected'
                            };

                            onReportEvent(EVENT_TRACER.SERVICE.CapturedImage, "Imagen capturada con exito en modo batch").catch();
                            this.stream.returnStatus(detectionResult.status);
                            this.autoCapturing(detectionResult);
                        } else {
                            this.stream.returnStatus(response.data.status || 'no_detected');
                        }
                    } catch (e) {
                        this.stream.returnStatus('error');
                    } finally {
                        this.batchFrames = [];
                        this.isCollectingBatch = false;
                    }
                }
            } else if (!enableBatch) {
                // Modo normal: detección y envío inmediato
                const detectionResult = await Detector.detectAllFaces(
                    this.videoElement,
                    this.croppedImage,
                    this.urlService,
                    this.documentType,
                    this.isFront,
                    this.processId,
                    this.useRegula,
                    this.config
                );

                if (detectionResult?.isCard) {
                    onReportEvent(EVENT_TRACER.SERVICE.CapturedImage, "Imagen capturada con exito").catch();
                    this.counterSuccessfulResults++;
                    this.autoCapturing(detectionResult);
                } else {
                    this.counterSuccessfulResults = 0;
                    this.stream.returnErrors(false);
                }
            }
        }
    }

    autoCapturing(result) {
        if (this.counterSuccessfulResults >= this.DEFAULT_NUMBER_SUCCESSFUL_RESULTS_FOR_AUTO_CAPTURING) {
            this.stream.autoCapturing({
                ...result,
                status: result.status,
                quality: result.isCard
            });
            this.counterSuccessfulResults = 0;
        }
    }

    checkNumberFaces(detectionResult) {
        return detectionResult.length === this.MAX_NUMBER_FACES;
    }

    checkProbability(detectionResult) {
        const score = detectionResult[0].detection.score;
        return score > +this.probabilityThreshold / 100;
    }

    checkFaceSize(detectionResult) {
        const faceBox = detectionResult[0].detection.box;
        const { width, height, area } = faceBox;
        const { imageHeight, imageWidth } = detectionResult[0].detection;
        const occupiedSize = 100 / ((imageHeight * imageWidth) / area);
        const minSide = Math.min(width / this.videoRatio, height / this.videoRatio);
        return (
            minSide > (this.isMobile ? this.MIN_FACE_SIZE_FOR_MOBILE : this.MIN_FACE_SIZE_FOR_DESKTOP) &&
            occupiedSize > (this.isMobile ? this.MIN_OCCUPIED_SPACE_FOR_MOBILE : this.MIN_OCCUPIED_SPACE_FOR_DESKTOP)
        );
    }

    checkFaceIndent(detectionResult) {
        const faceBox = detectionResult[0].detection.box;
        const { imageHeight, imageWidth } = detectionResult[0].detection;
        const { top, left, bottom, right } = faceBox;
        const topIndent = top;
        const leftIndent = left;
        const rightIndent = imageWidth - right;
        const bottomIndent = imageHeight - bottom;
        return !(
            topIndent < this.Y_OFFSET_FROM_FRAME ||
            leftIndent < this.X_OFFSET_FROM_FRAME ||
            rightIndent < this.X_OFFSET_FROM_FRAME ||
            bottomIndent < this.Y_OFFSET_FROM_FRAME
        );
    }

    drawFaces(data) {
        if (!this.innerCanvas) {
            this.createCanvasForDrawingMask(this.videoElement);
        }
        const canvas = this.innerCanvas;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
    }

    createCanvasForDrawingMask(video) {
        this.innerCanvas = document.createElement("canvas");
        this.innerCanvas.className = "canvas-on-video";
        this.innerCanvas.width = video.videoWidth;
        this.innerCanvas.height = video.videoHeight;
        this.videoElement.after(this.innerCanvas);
        if (this.innerCanvas.offsetWidth) {
            this.innerCanvas.setAttribute(
                "style",
                `margin-left:${-(this.innerCanvas.offsetWidth / 2)}px; margin-top: ${-(this.innerCanvas.offsetHeight / 2)}px;`
            );
        }
    }
}
